<template>
  <section v-if="isAuthenticated" class="dashboard">
    <div
      v-for="index in 6"
      :key="index"
      :class="['dashboard__background', `dashboard__background--${index}`]"
    ></div>
    <div v-if="isPageLoading" class="dashboard__wrapper">
      <ActionLoader />
    </div>
    <div v-else class="dashboard__wrapper">
      <UserDetails
        :userName="userName"
        :userInfo="userInfo"
        :currentAvatar="currentAvatar"
        :canManageMFA="canManageMFA"
        :isBusinessUser="isBusinessUser"
        :paymentInfo="paymentInfo"
        @is-updating-phone-number="isUpdatingPhoneNumber = true"
        @is-updating-mfa-method="isUpdatingMFAMethod = true"
        @is-managing-payment="isManagingPayment = true"
      />
      <div class="dashboard__menu">
        <div class="dashboard__options-wrapper">
          <div
            v-for="(option, i) in options"
            :key="`menu-option-${i}`"
            :class="[
              'btn',
              'btn--primary',
              'dashboard__option',
              option === currentOption && 'dashboard__option--selected',
            ]"
            @click="updateSelection(option)"
          >
            {{ option }}
          </div>
        </div>
        <div class="dashboard__options-wrapper--mobile">
          <Dropdown
            :options="options"
            :handleSelection="dropdownHandler"
            :selectedOption="{
              label: currentOption,
              code: currentOption,
            }"
          />
        </div>
        <Components
          :selection="currentOption"
          :isLoading="isComponentLoading"
          @is-managing-payment="isManagingPayment = true"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import UserDetails from '@/components/user-dashboard/UserDetails.vue'
import Components from '@/components/user-dashboard/Components.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Dropdown from '@/components/forms/elements/Dropdown.vue'

export default {
  name: 'UserDashboard',
  components: {
    UserDetails,
    Components,
    ActionLoader,
    Dropdown,
  },
  metaInfo() {
    return {
      title: `INE - Dashboard`,
      meta: [
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  },
  data() {
    return {
      isManagingPayment: false,
      isUpdatingPhoneNumber: false,
      isUpdatingMFAMethod: false,
      currentOption: 'Profile',
      routeMap: {
        learning: 'Learning',
        profile: 'Profile',
        analytics: 'Analytics',
        subscriptions: 'Subscriptions',
        certifications: 'Certifications',
        'early-renewal': 'Subscriptions',
      },
      isDesktop: false,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      profile: state => state.auth.userProfile,
      currentAvatar: state => state.auth.currentAvatar,
      paymentInfo: state => state.subscriptions.paymentInfo,
      isPageLoading: state => state.app.isPageLoading,
      isComponentLoading: state => state.app.isComponentLoading,
      subscriptions: state => state.subscriptions.currentSubscriptions,
      availablePlans: state => state.subscriptions.availablePlans,
      promotionsAvailable: state => state.subscriptions.promotionsAvailable,
    }),
    ...mapGetters({
      canManageMFA: 'auth/canManageMFA',
      isBusinessUser: 'auth/isBusinessUser',
      isFreeTrialUser: 'auth/isFreeTrialUser',
      hasBusinessUserAccount: 'business/isBusinessUser',
    }),
    userName() {
      const {
        profile: { first_name, last_name },
      } = this
      return `${first_name} ${last_name}`
    },
    userInfo() {
      const {
        email,
        preferred_phone_number: phoneNumber,
        mfa_method: mfaMethod,
      } = this.profile
      return { email, phoneNumber, mfaMethod }
    },
    isDifferentRoute() {
      const currentOption = this.currentOption.toLowerCase()
      const currentModePram = this.$route.params.mode
      return this.routeMap[currentModePram].toLowerCase() !== currentOption
    },
    showSubscriptionsTab() {
      return !this.hasBusinessUserAccount || !!this.subscriptions?.length
    },
    options() {
      if (this.showSubscriptionsTab) {
        return [
          'Profile',
          'Learning',
          'Analytics',
          'Subscriptions',
          'Certifications',
        ]
      }
      return ['Profile', 'Learning', 'Analytics', 'Certifications']
    },
  },
  watch: {
    showSubscriptionsTab(value) {
      if (!value && this.currentOption === this.routeMap.subscriptions)
        this.updateSelection(this.routeMap.profile)
    },
    currentOption() {
      if (this.isDifferentRoute) {
        const currentOption = this.currentOption.toLowerCase()
        this.$router.push({
          name: 'user-dashboard',
          params: { mode: currentOption },
        })
      }
      if (this.isDesktop) {
        this.$nextTick(() => {
          window.scrollTo(0, 650)
        })
      }
    },
    '$route.params.mode': {
      handler: 'updateCurrentOptionByParams',
      deep: true,
      immediate: true,
    },
    windowWidth(width) {
      this.isDesktop = width <= 1300
    },
  },
  async created() {
    const isEarlyYearPromoRoute = this.$route.params.mode === 'early-renewal'
    if (!this.isAuthenticated) {
      this.$router.push({ name: 'login' })
    } else {
      this.setIsPageLoading(true)
      await this.getSubscriptions()

      const {
        $route: {
          params: { mode },
        },
      } = this
      await this.getPaymentInfo()
      this.setIsPageLoading(false)
      if (this.isDifferentRoute) {
        const updatedMode = isEarlyYearPromoRoute ? 'subscriptions' : mode
        this.updateCurrentOptionByParams(
          updatedMode?.length ? updatedMode : 'profile'
        )
      }
      if (isEarlyYearPromoRoute) {
        try {
          // get available plans
          if (!this.availablePlans?.length) {
            await this.fetchAvailablePlans()
          }
          if (!this.promotionsAvailable?.length) {
            // get promotions
            await this.getPromotions()
          }
        } catch (error) {
          console.error(error)
        }
      }
      this.isUpgradePlanAllowed(true)
    }
  },
  mounted() {
    const dashboardMobileNavbar = document.querySelector(
      '.dashboard__options-wrapper--mobile'
    )
    window.addEventListener('scroll', event => {
      event.preventDefault()
      if (window.scrollY >= 640) {
        dashboardMobileNavbar?.classList.add('dashboard-collapse-navbar')
      } else {
        dashboardMobileNavbar?.classList.remove('dashboard-collapse-navbar')
      }
    })
    this.isDesktop = window.innerWidth <= 1024
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth
      })
    })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', () => null)
  },
  methods: {
    ...mapActions('app', ['setIsComponentLoading', 'setIsPageLoading']),
    ...mapActions('subscriptions', [
      'fetchAvailablePlans',
      'getPromotions',
      'getPaymentInfo',
      'getSubscriptions',
      'isUpgradePlanAllowed',
    ]),
    async updateSelection(option) {
      if (this.currentOption === option) return
      this.setIsComponentLoading(true)
      this.currentOption = option
    },
    updateCurrentOptionByParams(param) {
      const currentParam = param.toLowerCase()
      if (this.options.indexOf(this.routeMap[currentParam]) !== -1) {
        this.updateSelection(this.routeMap[currentParam])
      } else {
        this.updateSelection(this.routeMap.profile)
      }
    },
    updateProfileTab() {
      this.setIsComponentLoading(true)
    },
    dropdownHandler(e) {
      this.currentOption = e
      this.updateProfileTab()
    },
  },
}
</script>

<style lang="scss">
.dashboard {
  position: relative;
  padding: 63px 0;
  @include mobile {
    min-height: 1500px;
  }
  @include desktop {
    padding: 200px 0;
  }

  &__tab-title {
    margin: 20px 0 30px 0;
    font-size: rem(24);
    line-height: 32px;
    color: var(--white);
    font-weight: 400;
  }
  &__tab-sub-title {
    margin: 0 0 20px 0;
    font-size: rem(20);
    line-height: 26px;
    color: var(--white);
    font-weight: 700;
  }
  &__wrapper {
    max-width: 1240px;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    @include tablet {
      padding: 0 64px 0 40px;
    }
    @include desktop {
      padding: 0;
      flex-wrap: nowrap;
    }
  }
  &__menu {
    min-height: 100vh;
    width: 100%;
    margin: 0 20px;
    @include mobile {
      flex-grow: 1;
    }
  }
  &__options-wrapper {
    display: none;
    @include mobile {
      margin-bottom: -5px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
    &--mobile {
      position: relative;
      margin-bottom: 65px;
      width: 100%;
      @include mobile {
        display: none;
      }
    }
  }
  &__option {
    color: var(--perfect-gray);
    font-family: var(--font-bold);
    font-size: rem(12);
    line-height: 14px;
    padding: 10px 15px;
    cursor: pointer;
    margin: 0 20px 0 0;
    background: none;
    border-radius: 40px;
    &:last-child {
      margin: 0;
    }
    @include tablet {
      white-space: nowrap;
    }
    &:active {
      opacity: 1;
    }
    &--selected,
    &:hover,
    &:active {
      color: var(--charcoal);
      background: var(--white);
    }
  }
  &__loader-wrapper {
    width: 100%;
    margin: 150px auto 0 auto;
    text-align: center;
  }
  &__loader {
    margin: 0 auto;
  }
  &__components {
    width: 100%;
    margin: 35px auto 0 auto;
  }
  &-form {
    &-wrapper {
      display: flex;
      flex-direction: column;
    }
    &-row {
      display: flex;
      margin-bottom: 10px;
    }
    &-input span,
    &-input input {
      cursor: pointer;
    }
    &-input-label {
      color: var(--white);
      font-size: rem(14);
      line-height: 20px;
      margin-left: 10px;
      &--disabled {
        color: var(--light-blue-gray);
      }
    }
    &-checkbox {
      margin-bottom: 0;
    }
  }
  &-input-wrapper {
    position: relative;
    margin-bottom: 10px;
    max-width: 532.33px;
  }
  &-input {
    &__remove-wrapper {
      display: flex;
      align-items: center;
      font-size: rem(14);
      height: 36px;
      line-height: 20px;
      padding: 8px 16px;
      width: 100%;
      color: var(--carnation);
      cursor: pointer;
      @include mobile {
        margin: 0 0 10px 10px;
      }
    }
    &__remove-icon {
      width: 14px;
      margin-bottom: 2px;
    }
    &__remove-label {
      margin-left: 10px;
    }
  }
  &-select {
    appearance: none;
    position: relative;
    height: 46px;
    &-icon {
      content: '';
      font-size: 1rem;
      top: 14px;
      right: 13px;
      position: absolute;
      color: var(--light-blue-gray);
      pointer-events: none;
    }
    &__menu {
      padding: 12px 11px;
    }
  }
  &__action-icon {
    color: var(--white);
    font-size: 0.875rem;
    padding: 12.67px;
    border: solid 1px var(--emperor-gray);
    border-radius: 10px;
    margin-left: auto;
    height: 40px;
    width: 40px;
    cursor: pointer;
    &--hide {
      display: none;
    }
  }
  &__icons-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 90px;
    min-width: 90px;
    height: 40px;
    margin-left: 30px;
  }
  &__icon {
    &:first-child {
      border: none !important;
    }
    &--play {
      margin-left: 10px !important;
    }
    &--trash {
      &:hover {
        background: var(--white-10);
      }
      &:active {
        color: var(--white);
        background: var(--white-20);
      }
    }
  }
  &__progress-wrapper {
    width: 100%;
    text-align: center;
    border-radius: 10px;
    padding: 1px;
    background-color: var(--mid-gray-dark-25);
    height: 5px;
  }
  &__progress-bar {
    background-color: var(--white);
    height: 5px;
    border-radius: 10px;
    position: relative;
  }
  &__progress-no-trackable {
    &-container {
      width: 100%;
      display: flex;
      align-content: center;
      margin-left: 70px;
      &--flat {
        margin-left: 0;
        @include mobile {
          margin-left: 20px;
        }
      }
    }
    &-text {
      font-size: rem(14);
      line-height: rem(20);
      color: var(--light-blue-gray);
      &--flat {
        font-size: rem(12);
        line-height: rem(14);
      }
    }
  }
  &__badge {
    width: fit-content;
    text-align: center;
    font-size: rem(12);
    line-height: 14px;
    color: var(--white);
    padding: 9px 14px;
    background: var(--glass-dark);
    border-radius: 20px;
    border: 1px solid rgba(62, 63, 73, 0.2);
    text-transform: uppercase;
    white-space: nowrap;
    @include mobile {
      height: fit-content;
    }
  }
  &-collapse-navbar {
    width: 100vw;
    max-width: 100%;
    position: fixed;
    left: 0px;
    background: var(--black);
    padding: 10px 15px 10px 9px;
    top: 63px;
    z-index: 100;
    select {
      height: 43px;
      padding: 10px;
    }
    .dashboard-select-icon {
      top: 20px;
      right: 28px;
    }
  }
  &__background {
    position: absolute;
    pointer-events: none;
    opacity: 0.5;
    &--1 {
      display: none;
      @include mobile {
        display: block;
        width: 165px;
        height: 165px;
        top: 0;
        left: 0;
        background: var(--orange);
        filter: blur(170px);
      }
    }
    &--2 {
      width: 350px;
      height: 350px;
      top: 1000px;
      right: 50%;
      transform: translateX(50%);
      background: var(--dark-blue);
      mix-blend-mode: screen;
      filter: blur(194px);
      @include mobile {
        top: 0;
        right: 180px;
        filter: blur(250px);
      }
    }
    &--3 {
      width: 300px;
      height: 300px;
      left: -70%;
      top: 1650px;
      background: var(--orange);
      mix-blend-mode: screen;
      filter: blur(250px);
      @include mobile {
        width: 140px;
        height: 140px;
        right: -40px;
        top: 350px;
        filter: blur(135px);
      }
    }
    &--4 {
      display: none;
      @include mobile {
        display: block;
        width: 450px;
        height: 450px;
        left: -170px;
        top: 922px;
        background: var(--dark-blue);
        mix-blend-mode: screen;
        filter: blur(250px);
      }
    }
    &--5 {
      display: none;
      @include mobile {
        display: block;
        width: 370px;
        height: 370px;
        right: 1079px;
        top: 1100px;
        left: -200px;
        background: var(--orange);
        mix-blend-mode: screen;
        filter: blur(250px);
      }
    }
    &--6 {
      width: 370px;
      height: 370px;
      top: 632px;
      left: -200px;
      background: var(--orange);
      mix-blend-mode: screen;
      filter: blur(150px);
      z-index: -1;
      @include mobile {
        display: none;
      }
    }
  }
  &-item {
    &__action-loader {
      width: 100%;
      position: absolute;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: var(--black-50);
      border-radius: 5px;
      z-index: 100;
    }
  }
}
</style>
